import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { storage } from "@/lib/storage";
import toast from "react-hot-toast";
import { LogOut, ChevronDown, Menu, X, Gift } from "lucide-react";
import NotificationsDropdown from "@/components/notifications/NotificationsDropdown";
import { getNotifications } from "@/services/parent";
import { useDispatch } from 'react-redux';
import { updateUserData } from "@/lib/store/slices/UserSlice";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { setRefreshTokenCookie } from "@/lib/clientCookie";
import { logoutService } from '@/services/auth';
import { clearPermissions } from '@/lib/store/slices/PermissionsSlice';

const NAV_ITEMS = [
  { label: "Dashboard", path: "/dashboard" },
  { label: "Children", path: "/children" },
  { label: "Plans", path: "/parent-plans" },
  { label: "Leaves", path: "/leaves" },
  { label: "Track Delivery", path: "/parent-lunch-updates" },
];

export default function ParentHeader() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const userDetails = useCurrentUser();
  const dispatch = useDispatch();
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  let isLoggingOut = false;

  useEffect(() => { setShowMobileMenu(false); }, [pathname]);

  useEffect(() => {
    document.body.style.overflow = showMobileMenu ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [showMobileMenu]);

  const handleLogout = async () => {
    if (isLoggingOut) return;
    isLoggingOut = true;

    try {
      const refreshToken = storage.getRefreshToken();

      if (refreshToken) {
        await logoutService(refreshToken);
      }
    } catch (err) {
      console.error("Logout API failed:", err);
    } finally {
      storage.setRefreshToken('');
      setRefreshTokenCookie('');
      storage.logout();
      dispatch(updateUserData(null));
      dispatch(clearPermissions());

      toast.success("Logged out successfully");
       navigate("/login", { replace: true });

      isLoggingOut = false;
    }
  };

  return (
    <header className="sticky top-0 z-40" style={{ background: 'linear-gradient(135deg, #1e2530 0%, #2d3748 50%, #1e2530 100%)', boxShadow: '0 4px 24px rgba(0,0,0,0.4), 0 1px 0 rgba(255,255,255,0.05)' }}>

      {/* Top accent line */}
      <div className="h-0.5 w-full" style={{ background: 'linear-gradient(90deg, transparent, #d4a017, #f0c040, #d4a017, transparent)' }} />

      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 py-2.5 flex items-center justify-between gap-3">

        {/* Logo */}
        <Link to="/dashboard" className="flex items-center gap-2.5 flex-shrink-0 group">
          <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl overflow-hidden border border-mlb-gold/30 group-hover:border-mlb-gold/60 transition-colors" style={{ boxShadow: '0 0 12px rgba(212,160,23,0.15)' }}>
            <img src="/mlblogo.jpeg" alt="MLB" className="w-full h-full object-cover" />
          </div>
          <div className="xs:block lg:hidden xl:block">
            <h1 className="text-base sm:text-lg font-extrabold text-white tracking-tight leading-tight">Mera Lunch Box</h1>
            <p className="text-mlb-gold text-[9px] font-semibold tracking-widest uppercase leading-none">Taaza Khana, Ghar Ka Khana</p>
          </div>
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-0.5">
          {NAV_ITEMS.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`text-sm font-semibold transition-all px-3 py-2 rounded-lg whitespace-nowrap ${pathname === item.path
                  ? 'text-[#1e2530] bg-mlb-gold shadow-md'
                  : 'text-white/70 hover:text-white hover:bg-white/10'
                }`}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        {/* Right section */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <button onClick={() => navigate("/refer-a-friend")} className="bg-[#fdec29] rounded-2xl p-2 shadow-xl">
            <Gift className="w-6 h-6 text-[#333A45]" />
          </button>
          <NotificationsDropdown
            fetchFn={() => getNotifications(userDetails?.username ?? '')}
            viewAllPath="/notifications"
          />

          {/* User menu — desktop */}
          <div className="relative group hidden sm:block">
            <button className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg hover:bg-white/10 transition-colors">
              <div className="w-8 h-8 bg-mlb-gold rounded-full flex items-center justify-center text-sm font-bold text-[#1e2530] flex-shrink-0">
                {userDetails?.username?.[0]?.toUpperCase()}
              </div>
              <div className="text-left hidden md:block">
                <p className="text-sm text-white font-semibold leading-tight">{userDetails?.username}</p>
                <p className="text-[10px] text-mlb-gold leading-tight">Parent</p>
              </div>
              <ChevronDown className="w-3.5 h-3.5 text-white/50" />
            </button>

            <div className="absolute right-0 mt-2 w-52 rounded-xl overflow-hidden opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50" style={{ background: '#1e2530', border: '1px solid rgba(255,255,255,0.08)', boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }}>
              <div className="p-4 border-b border-white/8">
                <p className="text-sm font-bold text-white">{userDetails?.username}</p>
                <p className="text-xs text-white/50 truncate mt-0.5">{(userDetails as any)?.email}</p>
                <span className="mt-2 inline-block px-2 py-0.5 rounded-full text-xs font-bold bg-mlb-gold text-[#1e2530]">Parent</span>
              </div>
              <div className="p-2">
                <button type="button" onClick={handleLogout} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-400 hover:bg-white/8 hover:text-red-300 rounded-lg transition-colors">
                  <LogOut className="w-4 h-4" />
                  Logout
                </button>
              </div>
            </div>
          </div>

          {/* Hamburger */}
          <button
            onClick={() => setShowMobileMenu(prev => !prev)}
            className="md:hidden flex items-center justify-center w-9 h-9 rounded-lg hover:bg-white/10 transition-colors text-white/80 hover:text-white"
          >
            {showMobileMenu ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {showMobileMenu && (
        <div className="md:hidden absolute top-full left-0 right-0 z-50 border-t border-white/8 shadow-2xl max-h-[calc(100vh-60px)] overflow-y-auto" style={{ background: '#1a2030' }}>
          <div className="max-w-screen-2xl mx-auto px-4 py-3 space-y-1">

            {/* User info */}
            <div className="flex items-center gap-3 p-3 rounded-xl mb-3" style={{ background: 'rgba(255,255,255,0.05)' }}>
              <div className="w-10 h-10 bg-mlb-gold rounded-full flex items-center justify-center text-base font-bold text-[#1e2530] flex-shrink-0">
                {userDetails?.username?.[0]?.toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-white truncate">{userDetails?.username}</p>
                <p className="text-xs text-mlb-gold">Parent</p>
              </div>
              <button type='button' onClick={handleLogout} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-red-400 hover:bg-white/10 transition-colors">
                <LogOut className="w-3.5 h-3.5" />
                Logout
              </button>
            </div>

            {NAV_ITEMS.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors ${pathname === item.path
                    ? 'bg-mlb-gold text-[#1e2530]'
                    : 'text-white/75 hover:text-white hover:bg-white/8'
                  }`}
              >
                {item.label}
              </Link>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}
